import { useState, useEffect } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'
import { Badge } from '@/components/ui/badge'
import { Gift, Calendar } from 'lucide-react'
import { format } from 'date-fns'
import { de } from 'date-fns/locale'

interface StampEntry {
  id: string
  created_at: string
  points_awarded: number
  referral_customer_id?: string
}

interface ReferralStampHistoryProps {
  customerId: string
  tenantId: string
}

const ReferralStampHistory = ({ customerId, tenantId }: ReferralStampHistoryProps) => {
  const { toast } = useToast()
  const [stamps, setStamps] = useState<StampEntry[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchReferralStamps()
  }, [customerId, tenantId])

  const fetchReferralStamps = async () => {
    try {
      const { data, error } = await supabase
        .from('stamps')
        .select('*')
        .eq('customer_id', customerId)
        .eq('tenant_id', tenantId)
        .eq('stamp_type', 'referral')
        .order('created_at', { ascending: false })
        .limit(10)

      if (error) throw error
      setStamps(data || [])
    } catch (error) {
      console.error('Error fetching referral stamps:', error)
      toast({
        variant: "destructive",
        title: "Fehler",
        description: "Empfehlungshistorie konnte nicht geladen werden.",
      })
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return <div className="text-center py-4">Wird geladen...</div>
  }

  if (stamps.length === 0) {
    return (
      <div className="text-center py-8">
        <Gift className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
        <h3 className="text-lg font-semibold mb-2">Noch keine Empfehlungen</h3>
        <p className="text-muted-foreground">
          Empfehlen Sie uns weiter und sammeln Sie Ihre ersten Empfehlungsstempel!
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {stamps.map((stamp) => (
        <div key={stamp.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
          <div className="flex items-center gap-3">
            <Gift className="h-5 w-5 text-accent" />
            <div>
              <p className="font-medium">Empfehlung erhalten</p>
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {format(new Date(stamp.created_at), 'dd.MM.yyyy HH:mm', { locale: de })}
                </span>
              </div>
            </div>
          </div>
          <Badge variant="secondary">
            +{stamp.points_awarded} Punkt{stamp.points_awarded !== 1 ? 'e' : ''}
          </Badge>
        </div>
      ))}
    </div>
  )
}

export default ReferralStampHistory